// src/theme/enhancements.js
import { alpha } from '@mui/material/styles';

/**
 * Theme enhancements to standardize UI components and reduce inline styling
 */

/**
 * Common layout patterns to replace inline sx props
 */
export const layoutTokens = {
  // Flexbox layouts
  flex: {
    row: {
      display: 'flex',
      alignItems: 'center',
    },
    column: {
      display: 'flex',
      flexDirection: 'column',
    },
    between: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    center: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    end: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    wrap: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
  },

  // Section spacing
  spacing: {
    section: 4, // 32px
    subsection: 3, // 24px
    element: 2, // 16px
    item: 1, // 8px
    compact: 0.5, // 4px
  },

  // Container layouts
  container: {
    page: {
      width: '100%',
      maxWidth: '1200px',
      margin: '0 auto',
      padding: 3,
    },
    card: {
      padding: 3,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    section: {
      marginBottom: 4,
      position: 'relative',
    },
  },
};

/**
 * Extended opacity/alpha values for consistent transparency
 */
export const alphaTokens = {
  hover: 0.08,
  selected: 0.12,
  disabled: 0.38,
  focus: 0.12,
  border: 0.12,
  divider: 0.05,
  background: {
    paper: 0.96,
    light: 0.05,
    medium: 0.15,
    dark: 0.3,
  },
  soft: 0.08,
};

/**
 * Border styles for consistent UI elements
 */
export const borderTokens = {
  dashed: (theme, color = 'divider') => ({
    border: `1px dashed ${theme.palette[color] || theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  }),
  light: (theme) => ({
    border: `1px solid ${alpha(theme.palette.divider, alphaTokens.border)}`,
    borderRadius: theme.shape.borderRadius,
  }),
  highlight: (theme) => ({
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
  }),
  gradient: (theme) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 1,
      background: `linear-gradient(90deg, ${alpha(theme.palette.divider, 0)}, ${alpha(theme.palette.divider, 0.7)}, ${alpha(theme.palette.divider, 0)})`,
    },
  }),
};

/**
 * Standardized background styles
 */
export const backgroundTokens = {
  subtle: (theme, color = 'primary') => ({
    backgroundColor: alpha(theme.palette[color].main, alphaTokens.background.light),
  }),
  medium: (theme, color = 'primary') => ({
    backgroundColor: alpha(theme.palette[color].main, alphaTokens.background.medium),
  }),
  paper: (theme) => ({
    backgroundColor: theme.palette.background.paper,
  }),
  paperLight: (theme) => ({
    backgroundColor: alpha(theme.palette.background.paper, alphaTokens.background.paper),
  }),
  highlight: (theme) => ({
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)}, ${alpha(theme.palette.primary.main, 0.2)})`,
  }),
};

/**
 * Improved card style tokens
 */
export const cardTokens = {
  root: (theme) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: theme.shape.borderRadius * 2,
    transition: theme.aghrba?.tokens?.transitions?.standard || 'all 0.3s ease',
    background: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, alphaTokens.border)}`,
    '&:hover': {
      boxShadow: theme.shadows[3],
      transform: 'translateY(-4px)',
    },
  }),
  container: {
    padding: 3,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    '&:last-child': { 
      paddingBottom: 3 
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 2,
  },
  interactive: (theme) => ({
    '&:hover': {
      boxShadow: theme.shadows[8],
      transform: 'translateY(-4px)',
      borderColor: alpha(theme.palette.primary.main, 0.3),
    },
    cursor: 'pointer',
  }),
  topBorder: (color) => ({
    borderTop: `4px solid ${color}`,
  }),
};

/**
 * Chip style tokens
 */
export const chipTokens = {
  sizes: {
    small: {
      height: 24,
      fontSize: '0.75rem',
      '& .MuiChip-label': {
        padding: '0.5px 8px',
      },
    },
    medium: {
      height: 32,
      fontSize: '0.875rem',
    },
    large: {
      height: 40,
      fontSize: '0.875rem',
    },
  },
  status: (theme, color = 'default') => {
    const colorMap = {
      default: theme.palette.text.primary,
      primary: theme.palette.primary.main,
      secondary: theme.palette.secondary.main,
      success: theme.palette.success.main,
      error: theme.palette.error.main,
      warning: theme.palette.warning.main,
      info: theme.palette.info.main,
    };
    
    return {
      backgroundColor: alpha(colorMap[color], alphaTokens.background.light),
      color: colorMap[color],
      fontWeight: 500,
    };
  },
  subtle: (theme) => ({
    backgroundColor: theme.palette.mode === 'light'
      ? alpha(theme.palette.text.primary, 0.08)
      : alpha(theme.palette.text.primary, 0.05),
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    backdropFilter: 'blur(4px)',
    borderRadius: '16px',
    color: theme.palette.text.primary,
    fontWeight: 500,
  }),
};

/**
 * Typography style tokens
 */
export const textTokens = {
  truncate: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  lineClamp: (lines = 2) => ({
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  }),
  weight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  tabLabel: {
    fontWeight: 500,
    letterSpacing: '0.025em',
  },
  sectionTitle: {
    fontWeight: 600,
    letterSpacing: '-0.01em',
  },
};

/**
 * Creates the extended component tokens for the theme
 */
/**
 * Transition tokens for consistent animations
 */
export const transitionTokens = {
  standard: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  fast: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  slow: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
};

// Typography adjustments for better visibility
export const typographyAdjustments = {
  body1: (theme) => ({
    color: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.9)
      : alpha(theme.palette.common.black, 0.87),
  }),
  body2: (theme) => ({
    color: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.8)
      : alpha(theme.palette.common.black, 0.75),
  }),
  subtitle1: (theme) => ({
    color: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.9)
      : alpha(theme.palette.common.black, 0.87),
    fontWeight: 500,
  }),
  subtitle2: (theme) => ({
    color: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.9)
      : alpha(theme.palette.common.black, 0.87),
    fontWeight: 500,
  }),
  caption: (theme) => ({
    color: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.6)
      : alpha(theme.palette.common.black, 0.6),
  }),
};

export const createExtendedTokens = (theme) => {
  return {
    // Keep existing tokens
    ...theme.aghrba.tokens,

    // Add new tokens
    layout: layoutTokens,
    alpha: alphaTokens,
    borders: borderTokens,
    backgrounds: backgroundTokens,
    cards: cardTokens,
    chips: chipTokens,
    text: textTokens,
    transitions: transitionTokens,
    typography: typographyAdjustments,
  };
};

/**
 * Helper function to create common components with consistent styling
 * @param {object} theme - MUI theme object
 * @returns {object} - Styled component configuration
 */
// Define button token styles
export const buttonTokens = {
  rounded: {
    borderRadius: '20px',
    textTransform: 'none',
    fontWeight: 500,
  },
  elevated: (theme) => ({
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    '&:hover': {
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
      transform: 'translateY(-2px)',
    },
    transition: 'all 0.2s ease',
  }),
  soft: (theme, color = 'primary') => {
    const colorMap = {
      primary: theme.palette.primary.main,
      secondary: theme.palette.secondary.main,
      success: theme.palette.success.main,
      error: theme.palette.error.main,
      warning: theme.palette.warning.main,
      info: theme.palette.info.main,
    };
    const mainColor = colorMap[color] || colorMap.primary;

    return {
      backgroundColor: alpha(mainColor, 0.1),
      color: theme.palette.mode === 'dark' ? theme.palette.common.white : mainColor,
      '&:hover': {
        backgroundColor: alpha(mainColor, 0.2),
      },
    };
  },
};

// Define form field token styles
export const formFieldTokens = {
  rounded: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '16px',
    },
  },
  filled: (theme) => ({
    '& .MuiOutlinedInput-root': {
      backgroundColor: alpha(theme.palette.background.paper, 0.6),
      backdropFilter: 'blur(8px)',
      '&:hover': {
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
      },
      '&.Mui-focused': {
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
      },
    },
  }),
  branded: (theme, color = 'primary') => {
    const mainColor = theme.palette[color]?.main || theme.palette.primary.main;

    return {
      '& .MuiOutlinedInput-root': {
        '&.Mui-focused': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: mainColor,
            borderWidth: 2,
          },
        },
      },
      '& .MuiFormLabel-root.Mui-focused': {
        color: mainColor,
      },
    };
  },
};

// Define dialog token styles
export const dialogTokens = {
  rounded: {
    '& .MuiPaper-root': {
      borderRadius: '16px',
    },
  },
  branded: (theme, color = 'primary') => {
    const mainColor = theme.palette[color]?.main || theme.palette.primary.main;

    return {
      '& .MuiDialogTitle-root': {
        backgroundColor: alpha(mainColor, 0.05),
        borderBottom: `1px solid ${alpha(mainColor, 0.1)}`,
      },
      '& .MuiDialogActions-root': {
        borderTop: `1px solid ${theme.palette.divider}`,
      },
    };
  },
};

export const createStyledComponents = (theme) => {
  const { palette, aghrba } = theme;
  const mode = palette.mode;

  return {
    // Typography styling for better visibility
    MuiTypography: {
      styleOverrides: {
        root: {
          color: theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.9)
            : alpha(theme.palette.common.black, 0.87),
        },
        body1: typographyAdjustments.body1(theme),
        body2: typographyAdjustments.body2(theme),
        subtitle1: typographyAdjustments.subtitle1(theme),
        subtitle2: typographyAdjustments.subtitle2(theme),
        caption: typographyAdjustments.caption(theme),
      },
    },
    MuiBox: {
      defaultProps: {
        sx: {
          borderRadius: theme.shape.borderRadius,
        },
      },
      styleOverrides: {
        root: {
          // Let Box keep its default styling
        },
      },
      variants: [
        {
          props: { variant: 'flexRow' },
          style: layoutTokens.flex.row,
        },
        {
          props: { variant: 'flexColumn' },
          style: layoutTokens.flex.column,
        },
        {
          props: { variant: 'flexBetween' },
          style: layoutTokens.flex.between,
        },
        {
          props: { variant: 'flexCenter' },
          style: layoutTokens.flex.center,
        },
        {
          props: { variant: 'section' },
          style: layoutTokens.container.section,
        },
      ],
    },
    MuiCard: {
      variants: [
        {
          props: { variant: 'entity' },
          style: {
            ...cardTokens.root(theme),
            position: 'relative',
          },
        },
        {
          props: { variant: 'interactive' },
          style: {
            ...cardTokens.root(theme),
            ...cardTokens.interactive(theme),
          },
        },
        {
          props: { variant: 'hover' },
          style: {
            '&:hover': {
              boxShadow: mode === 'dark'
                ? '0 8px 16px rgba(0, 0, 0, 0.25)'
                : '0 8px 16px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-4px)',
            },
          },
        },
      ],
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          '&.MuiChip-outlined': {
            borderWidth: theme.palette.mode === 'light' ? '1.5px' : '1px',
          },
          '&.MuiChip-filled': {
            color: theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.9)
              : alpha(theme.palette.common.black, 0.87),
          },
        },
        label: {
          fontSize: '0.8125rem',
        },
      },
      variants: [
        {
          props: { variant: 'status' },
          style: {
            borderRadius: '16px',
            ...chipTokens.sizes.small,
            ...chipTokens.status(theme, 'default'),
          },
        },
        {
          props: { variant: 'status', color: 'primary' },
          style: chipTokens.status(theme, 'primary'),
        },
        {
          props: { variant: 'status', color: 'secondary' },
          style: chipTokens.status(theme, 'secondary'),
        },
        {
          props: { variant: 'status', color: 'success' },
          style: chipTokens.status(theme, 'success'),
        },
        {
          props: { variant: 'status', color: 'error' },
          style: chipTokens.status(theme, 'error'),
        },
        {
          props: { variant: 'status', color: 'warning' },
          style: chipTokens.status(theme, 'warning'),
        },
        {
          props: { variant: 'status', color: 'info' },
          style: chipTokens.status(theme, 'info'),
        },
        {
          props: { variant: 'subtle' },
          style: chipTokens.subtle(theme),
        },
      ],
    },
    MuiLinearProgress: {
      variants: [
        {
          props: { variant: 'entity' },
          style: {
            height: 6,
            borderRadius: '16px',
            backgroundColor: alpha(palette.background.light, 0.5),
            '& .MuiLinearProgress-bar': {
              borderRadius: '16px',
            },
          },
        },
      ],
    },
    MuiCardContent: {
      variants: [
        {
          props: { variant: 'entity' },
          style: cardTokens.container,
        },
      ],
    },
    // Button variants
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
          // Ensure contrast in buttons
          '&.MuiButton-outlined': {
            borderWidth: theme.palette.mode === 'light' ? '1.5px' : '1px',
            '&:not(.Mui-disabled)': {
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.9)
                : alpha(theme.palette.common.black, 0.8),
            },
          },
          '&.MuiButton-text': {
            '&:not(.Mui-disabled)': {
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.9)
                : alpha(theme.palette.common.black, 0.8),
            },
          },
        },
        contained: {
          boxShadow: theme.palette.mode === 'light' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
        },
      },
      variants: [
        {
          props: { variant: 'rounded' },
          style: buttonTokens.rounded,
        },
        {
          props: { variant: 'elevated' },
          style: buttonTokens.elevated(theme),
        },
        {
          props: { variant: 'soft', color: 'primary' },
          style: buttonTokens.soft(theme, 'primary'),
        },
        {
          props: { variant: 'soft', color: 'secondary' },
          style: buttonTokens.soft(theme, 'secondary'),
        },
        {
          props: { variant: 'soft', color: 'success' },
          style: buttonTokens.soft(theme, 'success'),
        },
        {
          props: { variant: 'soft', color: 'error' },
          style: buttonTokens.soft(theme, 'error'),
        },
        {
          props: { variant: 'soft', color: 'warning' },
          style: buttonTokens.soft(theme, 'warning'),
        },
        {
          props: { variant: 'soft', color: 'info' },
          style: buttonTokens.soft(theme, 'info'),
        },
      ],
    },
    // TextField variants
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            color: theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.7)
              : alpha(theme.palette.common.black, 0.6),
            '&.Mui-focused': {
              color: theme.palette.primary.main,
            },
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.23)
                : alpha(theme.palette.common.black, 0.23),
              borderWidth: theme.palette.mode === 'light' ? '1.5px' : '1px',
            },
          },
          '& .MuiInputBase-input': {
            color: theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.9)
              : alpha(theme.palette.common.black, 0.87),
          },
        },
      },
      variants: [
        {
          props: { variant: 'rounded' },
          style: formFieldTokens.rounded,
        },
        {
          props: { variant: 'filled' },
          style: formFieldTokens.filled(theme),
        },
        {
          props: { variant: 'branded', color: 'primary' },
          style: formFieldTokens.branded(theme, 'primary'),
        },
        {
          props: { variant: 'branded', color: 'secondary' },
          style: formFieldTokens.branded(theme, 'secondary'),
        },
      ],
    },
    // Dialog variants
    MuiDialog: {
      variants: [
        {
          props: { variant: 'rounded' },
          style: dialogTokens.rounded,
        },
        {
          props: { variant: 'branded', color: 'primary' },
          style: dialogTokens.branded(theme, 'primary'),
        },
        {
          props: { variant: 'branded', color: 'secondary' },
          style: dialogTokens.branded(theme, 'secondary'),
        },
      ],
    },
    // Tab styling
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 48,
          color: theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.7)
            : alpha(theme.palette.common.black, 0.7),
          '&.Mui-selected': {
            color: theme.palette.mode === 'dark'
              ? theme.palette.common.white
              : theme.palette.common.black,
            fontWeight: 600,
          },
          '&:hover': {
            color: theme.palette.mode === 'dark'
              ? theme.palette.common.white
              : theme.palette.common.black,
            opacity: 0.9,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
        },
        root: {
          '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary.main,
          },
        },
      },
    },
  };
};